import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Button } from './ui/button';
import { Menu, X, Home, BookOpen, Users, Play, Code, GraduationCap, User, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getCurrentUserType } from '../../utils/supabase/auth';

export function QuickNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [userType, setUserType] = useState<'teacher' | 'student' | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUserType = async () => {
      try {
        const type = await getCurrentUserType();
        setUserType(type);
      } catch (error) {
        console.error('Error fetching user type:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserType();
  }, []);

  // Memoize nav items to ensure they update when location or userType changes
  const navItems = useMemo(() => {
    const commonItems = [
      { path: '/', label: 'Home', icon: Home },
      { path: '/demos', label: 'Demos', icon: Play },
      { path: '/join', label: 'Join Session', icon: Code },
      { path: '/student/dashboard', label: 'Student Dashboard', icon: User },
      { path: '/teacher/dashboard', label: 'Teacher Dashboard', icon: BookOpen },
    ];

    // Check if currently in a session based on URL path
    const inSession = location.pathname.includes('/join') || location.pathname.includes('/student/live');

    // If student is in a session, show session menu
    if (userType === 'student' && inSession) {
      return [
        { path: '/', label: 'Home', icon: Home },
        { path: '/student/dashboard', label: 'Student Dashboard', icon: User },
        { path: '/demos', label: 'Demos', icon: Play },
      ];
    }

    if (userType === 'teacher') {
      return [
        { path: '/', label: 'Home', icon: Home },
        { path: '/teacher/dashboard', label: 'Teacher Dashboard', icon: BookOpen },
        { path: '/demos', label: 'Demo', icon: Play },
      ];
    }

    // Default items if not logged in
    return commonItems;
  }, [userType, location.pathname]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Floating Action Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-12 h-12 rounded-full bg-card border border-border shadow-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:shadow-xl transition-all"
          aria-label="Quick navigation"
        >
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </motion.div>

      {/* Quick Nav Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/20 z-40"
            />

            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.96 }}
              transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
              className="fixed bottom-20 right-6 bg-card rounded-xl border border-border shadow-xl z-50 w-64 overflow-hidden"
            >
              {/* Header */}
              <div className="px-4 pt-4 pb-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Navigate
                </p>
              </div>

              {/* Nav Items */}
              <div className="px-2 pb-2">
                {navItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors group ${
                        active
                          ? 'bg-primary/8 text-primary'
                          : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${
                        active ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      <span className={`text-sm ${active ? 'font-medium' : ''}`}>
                        {item.label}
                      </span>
                      {active && (
                        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-border px-4 py-3">
                <p className="text-[11px] text-muted-foreground">
                  {userType === 'teacher' ? 'Teacher' : userType === 'student' ? 'Student' : 'Guest'} session
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}